import { HttpErrorResponse } from '@angular/common/http';

/**
 * Error normalizado de cualquier llamada al BFF. Es lo ÚNICO que ven los stores y componentes:
 * nadie debería inspeccionar `HttpErrorResponse` ni el envelope a mano.
 */
export class ApiError extends Error {
  constructor(
    /** Código HTTP (0 = sin conexión / bloqueado por CORS). */
    readonly status: number,
    /** Código de negocio del BFF (`invalid_transition`, `tax_id_exists`…) o uno genérico (`network_error`, `http_500`). */
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly correlationId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isConflict(): boolean {
    return this.status === 409;
  }
  get isValidation(): boolean {
    return this.status === 400 || this.status === 422;
  }
  get isServer(): boolean {
    return this.status >= 500;
  }

  /**
   * Convierte un `HttpErrorResponse` en `ApiError`. Entiende las DOS formas de error del BFF:
   *  - la del contrato actual: `{ ok:false, error:{ code, message, details }, correlationId }`
   *  - la estándar que documenta la arquitectura: `{ errorCode, message, details, traceId }`
   */
  static fromHttp(response: HttpErrorResponse): ApiError {
    if (response.status === 0) {
      return new ApiError(0, 'network_error', 'No hay conexión con el servidor');
    }

    const body = response.error as Record<string, unknown> | string | null;
    const envelope = body && typeof body === 'object' ? body : null;
    const error = envelope?.['error'] as Record<string, unknown> | null | undefined;

    const code = asString(error?.['code']) ?? asString(envelope?.['errorCode']) ?? `http_${response.status}`;
    const message =
      asString(error?.['message']) ?? asString(envelope?.['errorMessage']) ?? asString(envelope?.['message']) ?? response.statusText;

    const details = error?.['details'] ?? envelope?.['details'];
    const correlationId = asString(envelope?.['correlationId']) ?? asString(envelope?.['traceId']) ?? undefined;
    return new ApiError(response.status, code, message || `Error ${response.status}`, details, correlationId);
  }
}

const asString = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null);
